// pages/reports/OpeningBalanceReport.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { setToogleHeader } from '../../core/redux/action';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { 
    ChevronUp, 
    RotateCcw, 
    Download,
    Printer,
    DollarSign,
    TrendingUp,
    TrendingDown,
    PieChart,
    FileText,
    
    Users,
    Truck,
    Briefcase,
    Folder,
    RefreshCw,
    BarChart2,
    
    Search,
    CheckCircle,
    XCircle,
    AlertCircle
} from 'feather-icons-react/build/IconComponents';
import { reportActions } from '../../core/action/reportActions';

const MySwal = withReactContent(Swal);

// Helper function to safely parse currency values
const parseCurrency = (value) => {
    if (value === undefined || value === null) return 0;
    
    // Convert to string if it's not already
    const strValue = String(value);
    
    // Remove commas and trim
    const cleanedValue = strValue.replace(/,/g, '').trim();
    
    // Parse as float
    const parsed = parseFloat(cleanedValue);
    
    // Return 0 if NaN, otherwise the parsed value
    return isNaN(parsed) ? 0 : parsed;
};

const OpeningBalanceReport = () => {
    const dispatch = useDispatch();
    const data = useSelector((state) => state.toggle_header);
    
    // State
    const [reportData, setReportData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [asOnDate, setAsOnDate] = useState(new Date());
    const [activeTab, setActiveTab] = useState('summary');
    const [accountType, setAccountType] = useState('all');
    const [searchTerm, setSearchTerm] = useState('');
    
    // Format date for API
    const formatDateForAPI = (date) => {
        return date.toISOString().split('T')[0];
    };
    
    // Load report data
    const loadReport = useCallback(async () => {
        try {
            setLoading(true);
            
            const params = {
                as_on_date: formatDateForAPI(asOnDate)
            };
            
            const result = await reportActions.getOpeningBalanceReport(params);
            
            if (result.success) {
                setReportData(result.data);
            } else {
                MySwal.fire({
                    icon: 'error',
                    title: 'Error',
                    text: result.message || 'Failed to load report',
                    timer: 3000,
                    showConfirmButton: false
                });
            }
        } catch (error) {
            console.error('Error loading report:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to load report',
                timer: 3000,
                showConfirmButton: false
            });
        } finally {
            setLoading(false);
        }
    }, [asOnDate]);
    
    // Load on mount and when dependencies change
    useEffect(() => {
        loadReport();
    }, [loadReport]);
    
    // Handle export PDF
    const handleExportPDF = async () => {
        try {
            const params = {
                as_on_date: formatDateForAPI(asOnDate)
            };
            
            const result = await reportActions.exportToPDF('opening_balance', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `opening_balance_${formatDateForAPI(asOnDate)}.pdf`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting PDF:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export PDF',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle export Excel
    const handleExportExcel = async () => {
        try {
            const params = {
                as_on_date: formatDateForAPI(asOnDate)
            };
            
            const result = await reportActions.exportToExcel('opening_balance', params);
            
            if (result) {
                const url = window.URL.createObjectURL(new Blob([result.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', `opening_balance_${formatDateForAPI(asOnDate)}.xlsx`);
                document.body.appendChild(link);
                link.click();
                link.remove();
            }
        } catch (error) {
            console.error('Error exporting Excel:', error);
            MySwal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to export Excel',
                timer: 2000,
                showConfirmButton: false
            });
        }
    };
    
    // Handle print
    const handlePrint = () => {
        window.print();
    };
    
    // Calculate totals safely
    const calculateTotals = useCallback((items, field) => {
        if (!items || items.length === 0) return 0;
        
        return items.reduce((sum, item) => {
            const value = parseCurrency(item[field]);
            return sum + value;
        }, 0);
    }, []);
    
    // Filter accounts
    const getFilteredAccounts = () => {
        if (!reportData?.all_accounts) return [];
        
        let filtered = reportData.all_accounts;
        
        if (accountType !== 'all') {
            filtered = filtered.filter(acc => 
                acc.account_type?.toLowerCase() === accountType.toLowerCase()
            );
        }
        
        if (searchTerm) {
            filtered = filtered.filter(acc => 
                acc.account_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                acc.phone?.includes(searchTerm) ||
                acc.email?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }
        
        return filtered;
    };
    
    // Render filter bar
    const renderFilterBar = () => {
        const typeOptions = [
            { value: 'all', label: 'All Accounts' },
            { value: 'customer', label: 'Customers' },
            { value: 'supplier', label: 'Suppliers' },
            { value: 'bank', label: 'Bank Accounts' },
            { value: 'expense', label: 'Expense Categories' }
        ];
        
        return (
            <div className="card mb-4">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-3">
                            <label className="form-label">As On Date</label>
                            <DatePicker
                                selected={asOnDate}
                                onChange={setAsOnDate}
                                dateFormat="dd/MM/yyyy"
                                className="form-control"
                                maxDate={new Date()}
                            />
                        </div>
                        <div className="col-md-3">
                            <label className="form-label">Account Type</label>
                            <select 
                                className="form-select"
                                value={accountType}
                                onChange={(e) => setAccountType(e.target.value)}
                            >
                                {typeOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-4">
                            <label className="form-label">Search</label>
                            <div className="input-group">
                                <span className="input-group-text">
                                    <Search size={16} />
                                </span>
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search by name, phone, email..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                        </div>
                        <div className="col-md-2 d-flex align-items-end">
                            <button className="btn btn-primary w-100" onClick={loadReport}>
                                <RefreshCw size={16} className="me-2" />
                                Generate
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render summary cards with safe parsing
    const renderSummaryCards = () => {
        if (!reportData) return null;
        
        const { summary } = reportData;
        const totalDebit = parseCurrency(summary?.total_debit);
        const totalCredit = parseCurrency(summary?.total_credit);
        const totalOpening = parseCurrency(summary?.total_opening);
        const difference = parseCurrency(summary?.difference);
        const isBalanced = summary?.is_balanced || false;
        
        return (
            <div className="row mb-4">
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Opening</p>
                                    <h3 className="mb-0 text-primary">
                                        {reportActions.formatCurrency(totalOpening)}
                                    </h3>
                                    <small className="text-muted">As on {reportActions.formatDate(reportData.as_on_date)}</small>
                                </div>
                                <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                                    <DollarSign size={24} className="text-primary" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Debit</p>
                                    <h3 className="mb-0 text-danger">
                                        {reportActions.formatCurrency(totalDebit)}
                                    </h3>
                                </div>
                                <div className="bg-danger bg-opacity-10 rounded-circle p-3">
                                    <TrendingUp size={24} className="text-danger" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Total Credit</p>
                                    <h3 className="mb-0 text-success">
                                        {reportActions.formatCurrency(totalCredit)}
                                    </h3>
                                </div>
                                <div className="bg-success bg-opacity-10 rounded-circle p-3">
                                    <TrendingDown size={24} className="text-success" />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div className="col-xl-3 col-sm-6 col-12">
                    <div className="card">
                        <div className="card-body">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <p className="mb-1 text-muted">Status</p>
                                    <h3 className="mb-0">
                                        {isBalanced ? (
                                            <span className="text-success">
                                                <CheckCircle size={24} /> Balanced
                                            </span>
                                        ) : (
                                            <span className="text-danger">
                                                <XCircle size={24} /> Unbalanced
                                            </span>
                                        )}
                                    </h3>
                                    {!isBalanced && (
                                        <small className="text-muted">
                                            Diff: {reportActions.formatCurrency(difference)}
                                        </small>
                                    )}
                                </div>
                                <div className={`bg-${isBalanced ? 'success' : 'danger'} bg-opacity-10 rounded-circle p-3`}>
                                    {isBalanced ? (
                                        <CheckCircle size={24} className="text-success" />
                                    ) : (
                                        <AlertCircle size={24} className="text-danger" />
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render type summary cards with safe parsing
    const renderTypeSummary = () => {
        if (!reportData?.summary_by_type) return null;
        
        const { summary_by_type } = reportData;
        
        return (
            <div className="row mb-4">
                <div className="col-md-3 col-sm-6">
                    <div className="card bg-light">
                        <div className="card-body text-center">
                            <Users size={24} className="text-primary mb-2" />
                            <h6>Customers</h6>
                            <h5>{summary_by_type.customers?.count || 0}</h5>
                            <small className="text-muted">
                                {reportActions.formatCurrency(parseCurrency(summary_by_type.customers?.total))}
                            </small>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-3 col-sm-6">
                    <div className="card bg-light">
                        <div className="card-body text-center">
                            <Truck size={24} className="text-warning mb-2" />
                            <h6>Suppliers</h6>
                            <h5>{summary_by_type.suppliers?.count || 0}</h5>
                            <small className="text-muted">
                                {reportActions.formatCurrency(parseCurrency(summary_by_type.suppliers?.total))}
                            </small>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-3 col-sm-6">
                    <div className="card bg-light">
                        <div className="card-body text-center">
                            <Briefcase size={24} className="text-success mb-2" />
                            <h6>Bank Accounts</h6>
                            <h5>{summary_by_type.banks?.count || 0}</h5>
                            <small className="text-muted">
                                {reportActions.formatCurrency(parseCurrency(summary_by_type.banks?.total))}
                            </small>
                        </div>
                    </div>
                </div>
                
                <div className="col-md-3 col-sm-6">
                    <div className="card bg-light">
                        <div className="card-body text-center">
                            <Folder size={24} className="text-info mb-2" />
                            <h6>Expense Categories</h6>
                            <h5>{summary_by_type.expenses?.count || 0}</h5>
                            <small className="text-muted">
                                {reportActions.formatCurrency(parseCurrency(summary_by_type.expenses?.total))}
                            </small>
                        </div>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render tabs
    const renderTabs = () => {
        const tabs = [
            { id: 'summary', label: 'Summary', icon: <PieChart size={16} /> },
            { id: 'customers', label: 'Customers', icon: <Users size={16} /> },
            { id: 'suppliers', label: 'Suppliers', icon: <Truck size={16} /> },
            { id: 'banks', label: 'Bank Accounts', icon: <Briefcase size={16} /> },
            { id: 'expenses', label: 'Expenses', icon: <Folder size={16} /> },
            { id: 'all', label: 'All Accounts', icon: <BarChart2 size={16} /> }
        ];
        
        return (
            <ul className="nav nav-tabs mb-4">
                {tabs.map(tab => (
                    <li className="nav-item" key={tab.id}>
                        <button
                            className={`nav-link ${activeTab === tab.id ? 'active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            {tab.icon}
                            <span className="ms-2">{tab.label}</span>
                        </button>
                    </li>
                ))}
            </ul>
        );
    };
    
    // Render customers tab with safe parsing
    const renderCustomersTab = () => {
        if (!reportData?.customers || reportData.customers.length === 0) {
            return (
                <div className="alert alert-info">
                    No customer opening balances found.
                </div>
            );
        }
        
        const totalBalance = calculateTotals(reportData.customers, 'opening_balance');
        const totalCredit = calculateTotals(reportData.customers, 'credit_balance');
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Customer Opening Balances</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Customer Name</th>
                                    <th>Phone</th>
                                    <th>Email</th>
                                    <th className="text-end">Opening Balance</th>
                                    <th className="text-end">Credit Balance</th>
                                    <th>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.customers.map((customer, idx) => {
                                    const openingBalance = parseCurrency(customer.opening_balance);
                                    const creditBalance = parseCurrency(customer.credit_balance);
                                    
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <strong>{customer.account_name}</strong>
                                            </td>
                                            <td>{customer.phone || '-'}</td>
                                            <td>{customer.email || '-'}</td>
                                            <td className={`text-end ${openingBalance >= 0 ? 'text-danger' : 'text-success'}`}>
                                                {reportActions.formatCurrency(openingBalance)}
                                            </td>
                                            <td className="text-end text-warning">
                                                {reportActions.formatCurrency(creditBalance)}
                                            </td>
                                            <td>
                                                {openingBalance > 0 ? (
                                                    <span className="badge bg-danger">Dr</span>
                                                ) : openingBalance < 0 ? (
                                                    <span className="badge bg-success">Cr</span>
                                                ) : (
                                                    <span className="badge bg-secondary">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th colSpan="3">Total</th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(totalBalance)}
                                    </th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(totalCredit)}
                                    </th>
                                    <th></th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render suppliers tab with safe parsing
    const renderSuppliersTab = () => {
        if (!reportData?.suppliers || reportData.suppliers.length === 0) {
            return (
                <div className="alert alert-info">
                    No supplier opening balances found.
                </div>
            );
        }
        
        const totalBalance = calculateTotals(reportData.suppliers, 'opening_balance');
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Supplier Opening Balances</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Supplier Name</th>
                                    <th>Phone</th>
                                    <th>Email</th>
                                    <th className="text-end">Opening Balance</th>
                                    <th>Type</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.suppliers.map((supplier, idx) => {
                                    const openingBalance = parseCurrency(supplier.opening_balance);
                                    
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <strong>{supplier.account_name}</strong>
                                            </td>
                                            <td>{supplier.phone || '-'}</td>
                                            <td>{supplier.email || '-'}</td>
                                            <td className={`text-end ${openingBalance >= 0 ? 'text-success' : 'text-danger'}`}>
                                                {reportActions.formatCurrency(openingBalance)}
                                            </td>
                                            <td>
                                                {openingBalance > 0 ? (
                                                    <span className="badge bg-success">Cr</span>
                                                ) : openingBalance < 0 ? (
                                                    <span className="badge bg-danger">Dr</span>
                                                ) : (
                                                    <span className="badge bg-secondary">-</span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th colSpan="3">Total</th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(totalBalance)}
                                    </th>
                                    <th></th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render banks tab with safe parsing
    const renderBanksTab = () => {
        if (!reportData?.banks || reportData.banks.length === 0) {
            return (
                <div className="alert alert-info">
                    No bank account opening balances found.
                </div>
            );
        }
        
        const totalOpening = calculateTotals(reportData.banks, 'opening_balance');
        const totalCurrent = calculateTotals(reportData.banks, 'current_balance');
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Bank Account Opening Balances</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Account Name</th>
                                    <th>Account Number</th>
                                    <th className="text-end">Opening Balance</th>
                                    <th className="text-end">Current Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.banks.map((bank, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <strong>{bank.account_name}</strong>
                                        </td>
                                        <td>{bank.account_number || '-'}</td>
                                        <td className="text-end text-danger">
                                            {reportActions.formatCurrency(parseCurrency(bank.opening_balance))}
                                        </td>
                                        <td className="text-end">
                                            {reportActions.formatCurrency(parseCurrency(bank.current_balance))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th colSpan="2">Total</th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(totalOpening)}
                                    </th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(totalCurrent)}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render expenses tab with safe parsing
    const renderExpensesTab = () => {
        if (!reportData?.expenses || reportData.expenses.length === 0) {
            return (
                <div className="alert alert-info">
                    No expense category opening balances found.
                </div>
            );
        }
        
        const totalBalance = calculateTotals(reportData.expenses, 'opening_balance');
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">Expense Category Opening Balances</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Category</th>
                                    <th className="text-end">Opening Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {reportData.expenses.map((expense, idx) => (
                                    <tr key={idx}>
                                        <td>
                                            <div className="d-flex align-items-center">
                                                <span 
                                                    className="badge me-2"
                                                    style={{ 
                                                        backgroundColor: expense.color + '20',
                                                        color: expense.color
                                                    }}
                                                >
                                                    {expense.icon}
                                                </span>
                                                <strong>{expense.account_name}</strong>
                                            </div>
                                        </td>
                                        <td className="text-end text-danger">
                                            {reportActions.formatCurrency(parseCurrency(expense.opening_balance))}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th>Total</th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(totalBalance)}
                                    </th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    // Render all accounts tab with safe parsing
    const renderAllAccountsTab = () => {
        const filteredAccounts = getFilteredAccounts();
        
        if (filteredAccounts.length === 0) {
            return (
                <div className="alert alert-info">
                    No accounts found matching your criteria.
                </div>
            );
        }
        
        const totalBalance = calculateTotals(filteredAccounts, 'opening_balance');
        
        return (
            <div className="card">
                <div className="card-header">
                    <h6 className="mb-0">All Accounts Opening Balances</h6>
                </div>
                <div className="card-body">
                    <div className="table-responsive">
                        <table className="table table-hover">
                            <thead className="table-light">
                                <tr>
                                    <th>Account Type</th>
                                    <th>Account Name</th>
                                    <th>Details</th>
                                    <th className="text-end">Opening Balance</th>
                                    <th>Dr/Cr</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredAccounts.map((account, idx) => {
                                    const openingBalance = parseCurrency(account.opening_balance);
                                    
                                    return (
                                        <tr key={idx}>
                                            <td>
                                                <span className={`badge ${
                                                    account.account_type === 'Customer' ? 'bg-primary' :
                                                    account.account_type === 'Supplier' ? 'bg-warning' :
                                                    account.account_type === 'Bank' ? 'bg-success' : 'bg-info'
                                                }`}>
                                                    {account.account_type}
                                                </span>
                                            </td>
                                            <td>
                                                <strong>{account.account_name}</strong>
                                            </td>
                                            <td>
                                                {account.phone && <div><small>{account.phone}</small></div>}
                                                {account.account_number && <div><small>{account.account_number}</small></div>}
                                            </td>
                                            <td className={`text-end ${
                                                account.balance_type === 'Debit' ? 'text-danger' : 'text-success'
                                            }`}>
                                                {reportActions.formatCurrency(openingBalance)}
                                            </td>
                                            <td>
                                                <span className={`badge ${
                                                    account.balance_type === 'Debit' ? 'bg-danger' : 'bg-success'
                                                }`}>
                                                    {account.balance_type === 'Debit' ? 'Dr' : 'Cr'}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                            <tfoot className="table-light">
                                <tr>
                                    <th colSpan="3">Total</th>
                                    <th className="text-end">
                                        {reportActions.formatCurrency(totalBalance)}
                                    </th>
                                    <th></th>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                </div>
            </div>
        );
    };
    
    const renderTooltip = useCallback((props, text) => (
        <Tooltip id={`tooltip-${text}`} {...props}>{text}</Tooltip>
    ), []);
    
    if (loading && !reportData) {
        return (
            <div className="page-wrapper">
                <div className="content d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
                    <div className="text-center">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5 className="text-muted">Loading Opening Balance Report...</h5>
                    </div>
                </div>
            </div>
        );
    }
    
    return (
        <div className="page-wrapper">
            <div className="content">
                {/* Header */}
                <div className="page-header">
                    <div className="add-item d-flex">
                        <div className="page-title">
                            <h4>Opening Balance Report</h4>
                            <h6>View opening balances for all ledger accounts</h6>
                        </div>
                    </div>
                    
                    <ul className="table-top-head">
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export PDF')}>
                                <button className="btn btn-outline-danger" onClick={handleExportPDF}>
                                    <Download size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Export Excel')}>
                                <button className="btn btn-outline-success" onClick={handleExportExcel}>
                                    <FileText size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Print')}>
                                <button className="btn btn-outline-secondary" onClick={handlePrint}>
                                    <Printer size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Refresh')}>
                                <button className="btn btn-outline-secondary" onClick={loadReport}>
                                    <RotateCcw size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                        <li>
                            <OverlayTrigger placement="top" overlay={(props) => renderTooltip(props, 'Collapse')}>
                                <button
                                    className={data ? "btn btn-outline-secondary active" : "btn btn-outline-secondary"}
                                    onClick={() => dispatch(setToogleHeader(!data))}
                                >
                                    <ChevronUp size={18} />
                                </button>
                            </OverlayTrigger>
                        </li>
                    </ul>
                </div>
                
                {/* As On Info */}
                <div className="row mb-3">
                    <div className="col-md-12">
                        <div className="card bg-light">
                            <div className="card-body py-2">
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <strong>Opening Balances as on:</strong> {reportActions.formatDate(reportData?.as_on_date)}
                                    </div>
                                    <div>
                                        <strong>Total Accounts:</strong> {reportData?.all_accounts?.length || 0}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                
                {/* Filter Bar */}
                {renderFilterBar()}
                
                {/* Summary Cards */}
                {renderSummaryCards()}
                
                {/* Type Summary */}
                {renderTypeSummary()}
                
                {/* Tabs */}
                {renderTabs()}
                
                {/* Tab Content */}
                {activeTab === 'summary' && (
                    <div className="row">
                        <div className="col-md-12">
                            {renderCustomersTab()}
                            <div className="mt-4">
                                {renderSuppliersTab()}
                            </div>
                        </div>
                    </div>
                )}
                {activeTab === 'customers' && renderCustomersTab()}
                {activeTab === 'suppliers' && renderSuppliersTab()}
                {activeTab === 'banks' && renderBanksTab()}
                {activeTab === 'expenses' && renderExpensesTab()}
                {activeTab === 'all' && renderAllAccountsTab()}
            </div>
        </div>
    );
};

export default OpeningBalanceReport;